import React from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BlockIcon from "@mui/icons-material/Block";
import { AbsenceRequestResponseDto } from "../../../types/absence";
import { AbsenceRequestStatus } from "../../../constants/absenceEnums";
import { DataTable, Column, Action } from "../../../components/common/DataTable/DataTable";
import {
  AbsenceCategoryChip,
  AbsenceStatusChip,
  DateRangeDisplay,
} from "../../../components/absence";
import { getAbsenceCategoryColor, getAbsenceCategoryName, getAbsenceStatusColor, getAbsenceStatusName } from "../../../utils/statusColors";

interface AllRequestsListProps {
  requests: AbsenceRequestResponseDto[];
  isLoading: boolean;
  onViewDetailsClick: (request: AbsenceRequestResponseDto) => void;
  onRevokeClick?: (request: AbsenceRequestResponseDto) => void;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const AllRequestsList: React.FC<AllRequestsListProps> = ({
  requests,
  isLoading,
  onViewDetailsClick,
  onRevokeClick,
  totalCount,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
}) => {
  const truncateText = (text: string | null, maxLength: number = 50): string => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const columns: Column[] = [
    {
      field: "employeeName",
      headerName: "Employee",
      flex: 1.5,
    },
    {
      field: "absenceCategory",
      headerName: "Type",
      flex: 1,
      renderCell: (value: number) => (
        <span style={{ 
          color: getAbsenceCategoryColor(value),
          fontWeight: 'bold'
        }}>
          {getAbsenceCategoryName(value)}
        </span>
      ),
    },
    {
      field: "startDate",
      headerName: "Dates",
      flex: 2,
      renderCell: (value: string, row: AbsenceRequestResponseDto) => (
        <DateRangeDisplay
          startDate={row.startDate}
          endDate={row.endDate}
          days={row.daysDeducted}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (value: number) => (
        <span style={{ 
          color: getAbsenceStatusColor(value),
          fontWeight: 'bold'
        }}>
          {getAbsenceStatusName(value)}
        </span>
      ),
    },
    {
      field: "requestReason",
      headerName: "Reason",
      flex: 1.5,
      renderCell: (value: string | null) => truncateText(value, 40),
    },
    {
      field: "createdAt",
      headerName: "Requested On",
      flex: 1,
      renderCell: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  const getActions = (
    request: AbsenceRequestResponseDto
  ): Action<AbsenceRequestResponseDto>[] => {
    const actions: Action<AbsenceRequestResponseDto>[] = [
      {
        icon: <VisibilityIcon />,
        label: "View Details",
        onClick: onViewDetailsClick,
        color: "primary",
      },
    ];

    // Add revoke button for approved requests
    if (request.status === AbsenceRequestStatus.Approved && onRevokeClick) {
      actions.push({
        icon: <BlockIcon />,
        label: "Revoke",
        onClick: onRevokeClick,
        color: "error",
      });
    }

    return actions;
  };

  return (
    <DataTable
      items={requests}
      columns={columns}
      actions={getActions}
      isLoading={isLoading}
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      onLoadMore={onLoadMore}
    />
  );
};

export default AllRequestsList;


